<template>
    <!-- form fields for the address(es) section -->
    <section class="form-fields-address pt-2 pb-0">
        <!-- if summary -->
        <div
            v-if="!addressStatus"
            class="b-1-gray-05 gray-0-bkg flex-row jc-space lh-20 p-3 br form-fields-address-saved"
        >
            <ul class="my-0">
                <li class="semibold">{{ addressName }}</li>
                <li class="semibold">{{ addressType.label }}</li>
                <li>{{ addressSettings.line1 }}</li>
                <li v-if="addressSettings.line2">{{ addressSettings.line2 }}</li>
                <li v-if="addressSettings.postalCode">{{ addressSettings.postalCode }}</li>
                <li v-if="addressSettings.city">{{ addressSettings.city }}</li>
                <li v-if="addressSettings.state">{{ addressSettings.state }}</li>
                <li v-if="addressSettings.country">{{ addressSettings.country }}</li>
            </ul>
            <a
                href="javascript:;"
                v-if="addressFieldsStatus !== 'pending'"
                class="link"
                type="secondary"
                @click="switchToEditView()"
                >{{ $l10n.__('Modifier') }}</a
            >
        </div>

        <!-- if update/create -->
        <div v-else>
            <!-- ADDRESS : NAME -->
            <el-form-item :prop="currentAddress.name">
                <span slot="label" class="gray-100">{{ $l10n.__(`Nom de l'adresse`) }}</span>
                <el-input
                    autocomplete="off"
                    v-model="addressName"
                    :data-bot="getDatabot('name', currentStore, 'address')"
                ></el-input>
            </el-form-item>

            <!-- ADDRESS : TYPE -->
            <el-form-item :prop="currentAddress.name">
                <span slot="label" class="gray-100">{{ $l10n.__(`Type`) }}</span>
                <el-select
                    v-model="addressType"
                    class="wp-10"
                    :data-bot="getDatabot('type', currentStore, 'address')"
                >
                    <el-option
                        v-for="option in addressTypeList"
                        :key="option.label"
                        :value="option"
                        :label="option.label"
                        :disabled="option.disable"
                    />
                </el-select>
            </el-form-item>

            <!-- ADDRESS  -->
            <address-input v-model="addressSettings">
                <template slot="manualAddress" slot-scope="{ data, updateCountry }">
                    <el-form-item class="my-2" :prop="currentAddress.settings.line1">
                        <span slot="label" class="gray-100">{{ $l10n.__('Adresse') }}</span>
                        <el-input
                            v-model="data.line1"
                            autocomplete="off"
                            :data-bot="getDatabot('address', currentStore, 'address')"
                        ></el-input>
                    </el-form-item>

                    <el-form-item
                        :prop="currentAddress.settings.line2"
                        :label="$t.__('common.labels.more_address_informations')"
                    >
                        <el-input
                            v-model="data.line2"
                            autocomplete="off"
                            :data-bot="getDatabot('more-infos', currentStore, 'address')"
                        ></el-input>
                    </el-form-item>

                    <el-form-item class="wp-5" :prop="currentAddress.settings.postalCode">
                        <span slot="label" class="gray-100">{{ $l10n.__('Code postal') }}</span>
                        <el-input
                            type="number"
                            class="wp-10"
                            :controls="false"
                            v-model="data.postalCode"
                            autocomplete="off"
                            :data-bot="getDatabot('zip', currentStore, 'address')"
                        ></el-input>
                    </el-form-item>

                    <el-form-item :prop="currentAddress.settings.city">
                        <span slot="label" class="gray-100">{{ $l10n.__('Ville') }}</span>
                        <el-input
                            v-model="data.city"
                            autocomplete="off"
                            :data-bot="getDatabot('country', currentStore, 'city')"
                        ></el-input>
                    </el-form-item>

                    <el-form-item
                        v-if="updateState"
                        :prop="currentAddress.settings.state"
                        :label="$t.__('common.labels.state')"
                    >
                        <el-input v-model="data.state" autocomplete="off"></el-input>
                    </el-form-item>

                    <el-form-item :prop="currentAddress.settings.country">
                        <span slot="label" class="gray-100">{{ $l10n.__('Pays') }}</span>
                        <country-input
                            :data-bot="getDatabot('country', currentStore, 'address')"
                            v-bind:default-value="{ code: data.countryCode, name: data.country }"
                            @change="updateCountry"
                        >
                        </country-input>
                    </el-form-item>
                </template>

                <!--  ADDRESS : SEARCH ALGOLIA -->
                <template slot="algoliaWrapper" slot-scope="{ methods, props, data }">
                    <el-form-item
                        :prop="currentAddress.settings.line1"
                        :label="$t.__('common.labels.address')"
                        :class="{ hide: data.currentValue.manualMode }"
                    >
                        <algolia-input
                            class="algolia-input--no-icon"
                            :element-name="props.value._id"
                            place-type="address"
                            @bubble-address="methods.captureAddress"
                            :data-bot="getDatabot('address', currentStore, 'address')"
                        />
                        <CompassButton
                            :label="props.manuallyLabel"
                            type="tertiary"
                            :data-bot="getDatabot('address-link-add', currentStore, 'address')"
                            @click="methods.switchMode"
                        />
                    </el-form-item>
                </template>
            </address-input>
            <div class="flex-row">
                <CompassButton
                    :label="$t.__('common.actions.cancel')"
                    type="secondary"
                    @click="remove()"
                    class="wp-5 mt-2"
                    v-if="index > 0"
                />
                <CompassButton
                    :label="$t.__('common.actions.save')"
                    type="primary"
                    @click="saveUpdates()"
                    class="wp-5 mt-2"
                    :class="{ 'wp-10': index === 0 }"
                    :disabled="!validForm"
                    :data-bot="getDatabot('btn', currentStore, 'address')"
                />
            </div>
        </div>
    </section>
</template>

<script>
import AlgoliaInput from 'vcomponents/inputs/AlgoliaInput.vue';
import CountryInput from 'vcomponents/inputs/CountryInput.vue';
import AddressInput from 'vcomponents/inputs/custom/Address.vue';
import DataBotsFormsMixins from 'mixins/DataBotsFormsMixins';

export default {
    props: {
        currentStore: {
            type: String,
            required: true,
        },
        index: {
            type: Number,
            required: false,
            default: 0,
        },
    },
    components: {
        AddressInput,
        AlgoliaInput,
        CountryInput,
    },
    mixins: [DataBotsFormsMixins],
    data() {
        return {
            updateState: false,
        };
    },
    computed: {
        validForm() {
            if (
                this.addressSettings.city &&
                this.addressSettings.country &&
                this.addressSettings.line1 &&
                this.addressSettings.postalCode &&
                this.addressType &&
                this.addressName &&
                this.addressName.length > 0
            ) {
                return true;
            }
            return false;
        },
        currentAddress() {
            return this.$store.getters[`${this.currentStore}/getAddress`](this.index);
        },
        addressSettings: {
            get() {
                let settings = this.$store.getters[`${this.currentStore}/getAddressSettings`](
                    this.index,
                );
                if (settings.state) {
                    this.updateState = true;
                }
                return settings;
            },
            set(value) {
                this.$store.commit(`${this.currentStore}/setAddressSettings`, {
                    value: value,
                    index: this.index,
                });
            },
        },
        addressTypeList() {
            return this.$store.getters[`${this.currentStore}/getAddressesTypes`];
        },
        addressName: {
            get() {
                return this.$store.getters[`${this.currentStore}/getAddressName`](this.index);
            },
            set(value) {
                this.$store.commit(`${this.currentStore}/setAddressName`, {
                    value: value,
                    index: this.index,
                });
            },
        },
        addressType: {
            get() {
                return this.$store.getters[`${this.currentStore}/getAddressType`](this.index);
            },
            set(newValue) {
                const { value, label } = newValue;
                this.$store.dispatch(`${this.currentStore}/setAddressType`, {
                    value,
                    label,
                    index: this.index,
                });
            },
        },
        addressStatus() {
            return this.$store.getters[`${this.currentStore}/getAddressStatus`](this.index);
        },
        addressFieldsStatus() {
            return this.$store.getters[`${this.currentStore}/getAddressesFieldsStatus`];
        },
    },
    methods: {
        switchToEditView() {
            this.$store.commit(`${this.currentStore}/setAddressesFieldsStatus`, 'pending');
            this.$store.dispatch(`${this.currentStore}/checkAndSetAuthorizedAddressTypes`);
            this.$store.commit(`${this.currentStore}/setAddressStatus`, {
                value: true,
                index: this.index,
            });
        },
        remove() {
            this.$store.dispatch(`${this.currentStore}/removeAddress`, this.index);
        },
        saveUpdates() {
            this.$store.commit(`${this.currentStore}/setAddressStatus`, {
                value: false,
                index: this.index,
            });
            this.$store.commit(`${this.currentStore}/setAddressesFieldsStatus`, 'saved');
        },
    },
};
</script>

<style lang="scss">
.algolia-input--no-icon > .algolia-places > button[type='button'] {
    display: none;
}
.form-fields-address-saved {
    ul li {
        margin-bottom: 4px;
    }
    ul li:last-child {
        margin-bottom: 0;
    }
}
</style>
